// Port of new arty's components/arty/community/ask-fields.tsx (arty repo), with the accent
// classes from its ask-kinds.ts. Client divergences: copy is `arty-card` `community-ask.*`,
// colours are stock Tailwind greens and client's own tokens, and the spinner is lucide's.
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { AskExample } from '~/components/arty/community/AskExample';
import type { CommunityKind } from '~/lib/arty-actions/catalog';
import { cn } from '~/lib/cn';

/** One accent, the house green: it points (a pressed chip, the submit), never fills a region. */
const CHIP_ON = 'border-green-200 bg-green-50 text-green-700';
const CHIP_OFF = 'border-border text-foreground hover:bg-muted';
const SUBMIT = 'bg-green-600 text-white hover:bg-green-700';

/** Controls are 44px so the whole flow works on a phone, where most of it is used. */
export const FIELD_CLASS =
  'h-11 w-full rounded-xl border border-border bg-background px-3 text-base text-foreground outline-hidden placeholder:text-muted-foreground focus-visible:border-green-600 sm:text-sm';

function Caption({ text, hint, required }: { text: string; hint?: string; required?: boolean }) {
  return (
    <>
      {text}
      {required ? ' *' : ''}
      {hint ? <span className="ml-1 font-normal text-muted-foreground">{hint}</span> : null}
    </>
  );
}

export function Field({
  children,
  hint,
  htmlFor,
  label,
  required,
}: {
  children: ReactNode;
  hint?: string;
  htmlFor: string;
  label: string;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-foreground" htmlFor={htmlFor}>
        <Caption hint={hint} required={required} text={label} />
      </label>
      {children}
    </div>
  );
}

export function FieldSet({
  children,
  hint,
  legend,
  required,
}: {
  children: ReactNode;
  hint?: string;
  legend: string;
  required?: boolean;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-xs font-semibold text-foreground">
        <Caption hint={hint} required={required} text={legend} />
      </legend>
      {children}
    </fieldset>
  );
}

export function Chip({ children, onClick, pressed }: { children: ReactNode; onClick: () => void; pressed: boolean }) {
  return (
    <button
      aria-pressed={pressed}
      className={cn(
        'min-h-11 rounded-full border px-4 text-sm font-medium transition-colors',
        pressed ? CHIP_ON : CHIP_OFF
      )}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

export function DurationChips({
  choices,
  onChange,
  value,
}: {
  choices: number[];
  onChange: (days: number) => void;
  value: number;
}) {
  const { t } = useTranslation('arty-card');
  return (
    <FieldSet hint={t('community-ask.fields.open-for-hint')} legend={t('community-ask.fields.open-for')}>
      <div className="flex flex-wrap gap-2">
        {choices.map((days) => (
          <Chip key={days} onClick={() => onChange(days)} pressed={value === days}>
            {t('community-ask.fields.days', { days })}
          </Chip>
        ))}
      </div>
    </FieldSet>
  );
}

/**
 * The body of every ask form: what the artist gets back (with a worked example behind a
 * disclosure), the fields, the server's error, the submit.
 */
export function AskFormShell({
  canSubmit,
  children,
  error,
  kind,
  onSubmit,
  submitting,
}: {
  canSubmit: boolean;
  children: ReactNode;
  error: string | null;
  kind: CommunityKind;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const { t } = useTranslation('arty-card');
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <AskExample kind={kind} />
      {children}
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <button
        className={cn(
          'inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition-colors disabled:opacity-50',
          SUBMIT
        )}
        disabled={!canSubmit || submitting}
        type="submit"
      >
        {submitting ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
        {submitting ? t('community-ask.posting') : t(`community-ask.kinds.${kind}.submit`)}
      </button>
    </form>
  );
}
